import React, { useEffect, useMemo, useRef, useState } from 'react';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';
import { Button, Text } from '@fragments';
import { IconCreditCard, IconLock, IconRefresh } from '@tabler/icons-react';
import { studioMock } from '../../../../data/studioApi';
import { stripeFor } from '../../../../data/payments';
import './styles.scss';

// Stripe's embedded checkout page for the session the core created (`client_secret`,
// contract 1.8.0). Card data never touches the Studio. `onComplete` fires once Stripe
// has the payment; the grant still waits for the webhook (the dialog polls it).
export default function CheckoutPane({ checkout, publishableKey, total, onComplete, t }) {
  const done = useRef(onComplete);
  done.current = onComplete;
  const [attempt, setAttempt] = useState(0);
  const [failed, setFailed] = useState(false);
  const stripe = useMemo(() => (publishableKey && !studioMock ? stripeFor(publishableKey) : null), [publishableKey, attempt]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    let alive = true;
    setFailed(false);
    stripe?.catch(() => alive && setFailed(true));
    return () => { alive = false; };
  }, [stripe]);
  const options = useMemo(() => (checkout?.client_secret ? { clientSecret: checkout.client_secret, onComplete: () => done.current() } : null), [checkout?.client_secret]);

  if (!checkout) return <div className="studio-plan-checkout studio-plan-checkout--status"><Text.span contrast={6} loading>{t('checkoutLoading')}</Text.span></div>;
  if (studioMock) return <MockCheckout checkout={checkout} total={total} onPaid={() => done.current()} t={t} />;
  if (failed) {
    return <div className="studio-plan-checkout studio-plan-checkout--status" role="alert">
      <Text.span contrast={8}>{t('checkoutUnavailable')}</Text.span>
      <Button size="small" variant="rounded" color="overlay" startIcon={<IconRefresh />} onClick={() => setAttempt(n => n + 1)}>{t('retry')}</Button>
    </div>;
  }
  return <div className="studio-plan-checkout">
    <EmbeddedCheckoutProvider key={checkout.id} stripe={stripe} options={options}>
      <EmbeddedCheckout className="studio-plan-checkout__stripe" />
    </EmbeddedCheckoutProvider>
  </div>;
}

// `?mock=true`: no Stripe session exists, so a test form stands in for it. Paying
// marks the session paid; the mock's webhook grants a moment later.
function MockCheckout({ checkout, total, onPaid, t }) {
  const [paying, setPaying] = useState(false);
  const pay = async () => {
    setPaying(true);
    await studioMock.pay(checkout.id);
    onPaid();
  };
  return <div className="studio-plan-checkout studio-plan-checkout--mock">
    <div className="studio-plan-checkout__mock-head">
      <Text.label contrast={9}>{t('mockCheckoutTitle')}</Text.label>
      <Text.caption contrast={6} left={<IconLock />}>Stripe</Text.caption>
    </div>
    <div className="studio-plan-checkout__mock-field">
      <IconCreditCard aria-hidden="true" />
      <Text.span contrast={8}>4242 4242 4242 4242</Text.span>
      <Text.caption contrast={6}>12/34 · 123</Text.caption>
    </div>
    <Button fullWidth variant="rounded" color="inverse" loading={paying} onClick={pay}>{t('checkoutPay', { amount: total })}</Button>
    <Text.caption contrast={6}>{t('mockCheckoutHint')}</Text.caption>
  </div>;
}
